import { computed, onUnmounted, ref, type Ref } from 'vue'

import { DEFAULT_TIME_ZONE } from '@/stores/user'
import { dateFormat } from '@/utils/dateFormatPreference'
import { getUtcOffsetMinutes } from '@/utils/timezones'

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** US/UK leggono l'orologio in 12h con AM/PM, EU/ISO in 24h — stessa regola di `utils/datetime.ts`. */
function hour12FromPreference(): boolean {
  return dateFormat.value === 'US' || dateFormat.value === 'UK'
}

/** Compact "UTC+01" / "UTC+05:30" — the header has no room for "(UTC+01:00)". */
function formatUtcOffset(totalMinutes: number): string {
  const sign = totalMinutes < 0 ? '-' : '+'
  const abs = Math.abs(totalMinutes)
  const hh = pad(Math.floor(abs / 60))
  const mm = abs % 60
  return mm === 0 ? `UTC${sign}${hh}` : `UTC${sign}${hh}:${pad(mm)}`
}

/** HH:mm:ss, or hh:mm:ss AM/PM when the account's date format prefers a 12-hour clock. */
function formatHms(hour: number, minute: number, second: number): string {
  if (!hour12FromPreference()) return `${pad(hour)}:${pad(minute)}:${pad(second)}`

  const period = hour >= 12 ? 'PM' : 'AM'
  const twelveHour = hour % 12 || 12
  return `${pad(twelveHour)}:${pad(minute)}:${pad(second)} ${period}`
}

/** HH:mm:ss in an IANA zone, falling back to Europe/London if it is missing or unusable. */
export function formatInTimeZone(date: Date, timeZone: string | null): string {
  try {
    const raw = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone || DEFAULT_TIME_ZONE,
        // hourCycle h23 guarantees 00–23 (hour12:false can still yield "24").
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
        .formatToParts(date)
        .map((part) => [part.type, part.value]),
    )
    return formatHms(Number(raw.hour), Number(raw.minute), Number(raw.second))
  } catch {
    return timeZone ? formatInTimeZone(date, null) : ''
  }
}

/** UTC offset of the zone in use (e.g. "UTC+01"), not its IANA name. */
export function timeZoneLabel(timeZone: string | null, reference: Date): string {
  try {
    return formatUtcOffset(getUtcOffsetMinutes(timeZone || DEFAULT_TIME_ZONE, reference))
  } catch {
    return formatUtcOffset(getUtcOffsetMinutes(DEFAULT_TIME_ZONE, reference))
  }
}

/**
 * Ticking clock for the header.
 *
 * @param timeZone reactive IANA zone; `null` means "use the Europe/London default".
 */
export function useClock(timeZone: Ref<string | null>) {
  const now = ref(new Date())

  const interval = setInterval(() => {
    now.value = new Date()
  }, 1_000)

  onUnmounted(() => clearInterval(interval))

  return {
    // Recomputes when either the tick or the user's timezone changes.
    time: computed(() => formatInTimeZone(now.value, timeZone.value)),
    label: computed(() => timeZoneLabel(timeZone.value, now.value)),
  }
}
